import {
    Text3,
    IconWarningRegular,
    Stack,
    NavigationBar,
    skinVars,
    Text5,
    useDialog,
    useWindowSize,
} from '@telefonica/mistica';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './index.scss';
import { useNavigate, useSearchParams } from 'react-router-dom';
import OTPVerification from '../../../../components/OTPVerification';
import { generateOtpCookie, sendEscalationOTP } from '../../../../services/EscalationService';
import { setInfoError } from '../../../../utils/dataTransformation/errorMesssages';
import { AxiosError } from 'axios';
import { TrackingEvents } from '../../../../utils/dataTransformation/tracking';
import { isWebviewAndroid, isWebviewIOS } from '../../../../utils/navigation/controlDevices';
import {
    hideMobileCharacters,
    formatMobileNumber,
    hideEmailCharacters,
} from '../../../../utils/dataTransformation/unitsConversion';
import { retrieveUrlEscalation } from '../escalationStorage';
import HeaderProfile from '../../../../components/HeaderProfile';
import { HeaderConfigureLine } from '../../../../components/HeaderConfigureLine';

export interface EscalationContactsVerifyInterface {
    fromDesktop?: boolean;
    setComponentSelected?: any;
    flowDesktop?: string;
    contactDesktop?: any;
    contactsDesktop?: any;
    typeMediumDesktop?: string;
}

const EscalationContactsVerify: React.FC<EscalationContactsVerifyInterface> = ({
    fromDesktop,
    setComponentSelected,
    flowDesktop,
    contactDesktop,
    typeMediumDesktop,
}) => {
    const [showError, setShowError] = useState<boolean>(false);
    const [error, setError] = useState<number | undefined>(401);
    const { t } = useTranslation();
    const { trackingScreenView } = new TrackingEvents();
    const navigate = useNavigate();
    const [otp, setOtp] = useState('');
    const [incorrectCode, setIncorrectCode] = useState<boolean>(false);
    const size = useWindowSize();
    const [params] = useSearchParams();
    const contact = fromDesktop ? contactDesktop : params.get('contact')!;
    const typeMedium = fromDesktop ? typeMediumDesktop : params.get('type')!;
    const flow = fromDesktop ? flowDesktop : params.get('flow')!;
    const { dialog } = useDialog();

    const sendToNative = (url: string, description: string, err?: any) => {
        if (url === '/update-personal-data') {
            navigate(url + '?flow=' + flow);
        } else if (url === '/profile/update-password') {
            navigate(url + '?flow=' + flow);
        } else if (url === '/select-bank-account') {
            navigate(url + '?flow=' + flow);
        } else if (url.includes('configure-line/sim')) {
            navigate(url + '?flow=' + flow, { state: { escalation: 'ESCALATED' } });
        } else {
            const feedbackData = {
                stateFeedback: description,
                err: err,
            };
            navigate(url, { state: feedbackData });
        }
    };

    const escalationOTPSend = async () => {
        try {
            await sendEscalationOTP(
                contact,
                flow === 'mobile' || flow === 'email'
                    ? '6'
                    : flow === 'changePassword'
                    ? '8'
                    : flow === 'bankAccount' || flow === 'supportChangeBankAccount'
                    ? '1'
                    : '',
            );
        } catch (error) {
            sendToNative('/escalation/otp/feedback', 'ERROR', setInfoError(error));
        }
    };

    const escalationOTPValidate = () => {
        generateOtpCookie(otp)
            .then((response: any) => {
                if (response.status === 200) {
                    if (flow === 'changePassword') {
                        sendToNative('/profile/update-password', '');
                    } else if (flow === 'supportChangeBankAccount') {
                        sendToNative('/select-bank-account', '');
                    } else {
                        if (flow === 'mobile') {
                            sendToNative('/update-personal-data', '');
                        } else if (flow === 'email') {
                            sendToNative('/update-personal-data', '');
                        } else if (flow === 'bankAccount') {
                            sendToNative('/select-bank-account', '');
                        } else if (flow === 'esim') {
                            sendToNative(retrieveUrlEscalation().url, '');
                        } else if (flow === 'eSimQR') {
                            sendToNative(retrieveUrlEscalation().url, '');
                        }
                    }
                } else if (response.status === 400) {
                    setIncorrectCode(true);
                    setError(response?.status);
                    setShowError(true);
                }
            })
            .catch((error: AxiosError) => {
                if ([401, 403].includes(Number(error.response?.status))) {
                    const { id } = setInfoError(error);
                    dialog({
                        asset: <IconWarningRegular color={skinVars.colors.feedbackErrorBackground} size={64} />,
                        title: t('profileFeedbackErrorTitle'),
                        message: (() => {
                            return (
                                <Stack space={16}>
                                    <Text3 light color={skinVars.colors.borderHigh}>
                                        {t('errorValidateOTP')}
                                    </Text3>
                                    {id && (
                                        <Text3 light color={skinVars.colors.borderHigh}>
                                            {`${t('codeError')} ${id}`}
                                        </Text3>
                                    )}
                                </Stack>
                            );
                        })() as any,
                        acceptText: t('buttonUnderstoodFeedback'),
                    });
                } else {
                    sendToNative('/escalation/otp/feedback', 'ERROR', setInfoError(error));
                }
            });
    };

    useEffect(() => {
        if (!(isWebviewAndroid || isWebviewIOS)) {
            trackingScreenView();
        }
    }, []);

    if (size.width >= 1024) {
        return (
            <div className='desktop-update-contacts-validate-otp'>
                <div className='update-contacts-validate-otp'>
                    {flow === 'changePassword' && (
                        <div
                            className='desktop-update-contacts-validate-otp__header'
                            style={{ borderBottom: size.width >= 1024 ? `1px solid ${skinVars.colors.divider}` : '' }}
                        >
                            <HeaderProfile
                                title={t('profileChangePassword')}
                                breadcrumbs={[{ title: t('profileTitle'), url: '/profile' }]}
                            />
                        </div>
                    )}

                    {flow === 'mobile' && (
                        <div
                            className='desktop-update-contacts-validate-otp__header'
                            style={{ borderBottom: size.width >= 1024 ? `1px solid ${skinVars.colors.divider}` : '' }}
                        >
                            <HeaderProfile
                                title={t('personalDataUpdateMobileTitle')}
                                breadcrumbs={[
                                    { title: t('profileTitle'), url: '/profile' },
                                    { title: t('personalDataTitle2'), url: '/profile/personal-data' },
                                ]}
                            />
                        </div>
                    )}

                    {flow === 'email' && (
                        <div
                            className='desktop-update-contacts-validate-otp__header'
                            style={{ borderBottom: size.width >= 1024 ? `1px solid ${skinVars.colors.divider}` : '' }}
                        >
                            <HeaderProfile
                                title={t('personalDataUpdateEmailTitle')}
                                breadcrumbs={[
                                    { title: t('profileTitle'), url: '/profile' },
                                    { title: t('personalDataTitle2'), url: '/profile/personal-data' },
                                ]}
                            />
                        </div>
                    )}

                    {flow === 'bankAccount' && (
                        <div
                            className='desktop-update-contacts-validate-otp__header'
                            style={{ borderBottom: size.width >= 1024 ? `1px solid ${skinVars.colors.divider}` : '' }}
                        >
                            <HeaderProfile
                                title={t('supportContentRowChangeBankAccount')}
                                breadcrumbs={[
                                    { title: t('profileTitle'), url: '/profile' },
                                    { title: t('personalDataTitle2'), url: '/profile/personal-data' },
                                ]}
                            />
                        </div>
                    )}

                    {flow === 'supportChangeBankAccount' && (
                        <div
                            className='desktop-update-contacts-validate-otp__header'
                            style={{ borderBottom: size.width >= 1024 ? `1px solid ${skinVars.colors.divider}` : '' }}
                        >
                            <HeaderProfile
                                title={t('supportContentRowChangeBankAccount')}
                                breadcrumbs={[{ title: t('breadcrumbsSupport'), url: '/support' }]}
                            />
                        </div>
                    )}

                    {flow === 'eSim' && (
                        <div
                            className='desktop-update-contacts-validate-otp__header'
                            style={{ borderBottom: size.width >= 1024 ? `1px solid ${skinVars.colors.divider}` : '' }}
                        >
                            <HeaderConfigureLine />
                        </div>
                    )}

                    <div
                        className='update-contacts-escalation__content'
                        style={{
                            padding: size.width >= 1220 ? '40px 680px 0 104px' : '40px 416px 0 40px',
                        }}
                    >
                        <div className='update-contacts-escalation__content-title'>
                            <Text5>{t('otpFlowTitle')}</Text5>
                        </div>
                        <OTPVerification
                            title={`${t('updateContactsVerifiedOtp')} ${
                                typeMedium === 'mobile'
                                    ? t('updateContactsVerifiedOtpMobile')
                                    : t('updateContactsVerifiedOtpEmail')
                            } ${
                                typeMedium === 'mobile'
                                    ? hideMobileCharacters(formatMobileNumber(contact))
                                    : hideEmailCharacters(contact)
                            }.`}
                            subtitle1={''}
                            subtitle2={''}
                            contact={''}
                            handleSendAgain={escalationOTPSend}
                            setOTPValue={setOtp}
                            incorrectCode={incorrectCode}
                            handleButtonContinue={escalationOTPValidate}
                        ></OTPVerification>
                    </div>
                </div>
            </div>
        );
    } else {
        return (
            <div className='update-contacts-validate-otp'>
                {!(isWebviewIOS || isWebviewAndroid) && (
                    <div className='c-customer-agreement-navigationBar'>
                        <NavigationBar
                            onBack={() => {
                                navigate(-1);
                            }}
                            title={t('verificadeCode')}
                        />
                    </div>
                )}
                <OTPVerification
                    title={`${t('updateContactsVerifiedOtp')} ${
                        typeMedium === 'mobile'
                            ? t('updateContactsVerifiedOtpMobile')
                            : t('updateContactsVerifiedOtpEmail')
                    } ${
                        typeMedium === 'mobile'
                            ? hideMobileCharacters(formatMobileNumber(contact))
                            : hideEmailCharacters(contact)
                    }.`}
                    subtitle1={''}
                    subtitle2={''}
                    contact={''}
                    handleSendAgain={escalationOTPSend}
                    setOTPValue={setOtp}
                    incorrectCode={incorrectCode}
                    handleButtonContinue={escalationOTPValidate}
                ></OTPVerification>
            </div>
        );
    }
};

export default EscalationContactsVerify;
